import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import { useEffect, useState } from 'react';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [showShareTip, setShowShareTip] = useState(false);

  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    if (showShareTip) {
      const timer = setTimeout(() => setShowShareTip(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [showShareTip]);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-24 px-4 md:px-6 text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-200">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-3">文章未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您要查看的文章不存在或已被移除。</p>
            <Link
              to="/"
              className="inline-flex px-6 py-2.5 bg-primary-500 text-white rounded-lg font-medium text-sm hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const categoryInfo = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const catLabel = categoryInfo ? categoryInfo.name : article.category;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    dateModified: article.date,
    inLanguage: 'zh-CN',
    about: {
      '@type': 'Thing',
      name: article.tags[0],
    },
    keywords: article.tags.join(','),
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setShowShareTip(true);
    });
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${article.title} - 网络安全守护`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://wangluoanquan.cn/article/${article.slug}`}
        ogType="article"
      />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200/60">
            <div className="max-w-4xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: catLabel, href: `/category/${article.category}` },
                  { label: article.title },
                ]}
              />
            </div>
          </div>

          <article className="w-full px-4 md:px-6 py-10 md:py-14">
            <div className="max-w-3xl mx-auto">
              <header className="mb-10">
                <div className="flex items-center gap-3 mb-4">
                  <Link
                    to={`/category/${article.category}`}
                    className="text-xs px-3 py-1 rounded-full bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                  >
                    {catLabel}
                  </Link>
                  <span className="text-xs text-foreground-400">{article.date}</span>
                  <span className="text-xs text-foreground-400">阅读 {article.readTime}</span>
                </div>

                <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
                  {article.title}
                </h1>

                <p className="text-base text-foreground-500 leading-relaxed mb-6">
                  {article.excerpt}
                </p>

                <div className="flex items-center justify-between flex-wrap gap-3 pb-6 border-b border-background-200/60">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100">
                      <i className="ri-user-line text-sm text-primary-600"></i>
                    </div>
                    <span className="text-sm text-foreground-600">{article.author}</span>
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleCopyLink}
                      className="flex items-center gap-1.5 text-sm text-foreground-500 hover:text-primary-500 transition-colors duration-200 cursor-pointer relative"
                    >
                      <i className="ri-link text-base"></i>
                      <span>复制链接</span>
                      {showShareTip && (
                        <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-foreground-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                          已复制
                        </span>
                      )}
                    </button>
                    <button
                      onClick={handleCopyLink}
                      className="flex items-center gap-1.5 text-sm text-foreground-500 hover:text-primary-500 transition-colors duration-200 cursor-pointer"
                    >
                      <i className="ri-share-forward-line text-base"></i>
                      <span>分享</span>
                    </button>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mt-4">
                  {article.tags.map((tag) => (
                    <span key={tag} className="text-xs px-2.5 py-1 rounded-full bg-background-100 text-foreground-500 border border-background-200/60">
                      #{tag}
                    </span>
                  ))}
                </div>
              </header>

              <div
                className="article-content text-base"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-12 pt-8 border-t border-background-200/60">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-heading font-semibold text-foreground-900">相关文章</h3>
                  <Link
                    to={`/category/${article.category}`}
                    className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer"
                  >
                    查看全部 <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
                {relatedArticles.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {relatedArticles.map((ra) => (
                      <ArticleCard key={ra.slug} article={ra} />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-foreground-500">暂无相关文章</p>
                )}
              </div>
            </div>
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
}